import { useEffect, useRef, useState } from 'react';
import { Plus, Trash2, HelpCircle, Check, X, Paperclip, Upload } from 'lucide-react';
import { format } from 'date-fns';
import { api } from '../../api/client';
import { Badge } from '../ui/Badge';
import { Avatar } from '../ui/Avatar';

export function PedidosInfoSection({ demanda, podeCriar, podeResponder }) {
  const [lista, setLista] = useState([]);
  const [criando, setCriando] = useState(false);

  const load = () => api.get(`/demandas/${demanda.codigo}/pedidos-info`).then((r) => setLista(r.pedidos));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [demanda.codigo]);

  return (
    <div className="card">
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <strong><HelpCircle size={14} style={{ verticalAlign: 'middle' }} /> Pedidos de informação</strong>
          <div className="text-xs muted">Para pedir dados/arquivos ao solicitante — trava a demanda até ser resolvido.</div>
        </div>
        {podeCriar && !criando && (
          <button className="btn btn-secondary btn-sm" onClick={() => setCriando(true)}>
            <Plus size={14} /> Novo pedido
          </button>
        )}
      </div>
      <div className="card-body">
        {criando && (
          <CriarForm
            demandaId={demanda.codigo}
            onCancel={() => setCriando(false)}
            onCreated={() => { setCriando(false); load(); }}
          />
        )}
        {lista.length === 0 ? (
          <div className="text-sm muted">Nenhum pedido de informação no momento.</div>
        ) : (
          <div className="stack" style={{ gap: 12 }}>
            {lista.map((p) => <PedidoItem key={p.id} pedido={p} demandaId={demanda.codigo} podeCriar={podeCriar} podeResponder={podeResponder} onChange={load} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function CriarForm({ demandaId, onCancel, onCreated }) {
  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [itens, setItens] = useState(['']);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    const cleanItens = itens.map((s) => s.trim()).filter(Boolean);
    if (!titulo.trim() || cleanItens.length === 0) {
      setErr('Informe título e pelo menos um item.');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/demandas/${demandaId}/pedidos-info`, { titulo, descricao: descricao || null, itens: cleanItens });
      onCreated?.();
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="stack" style={{ gap: 10, marginBottom: 14, padding: 12, background: 'var(--c-warning-soft)', borderRadius: 10 }}>
      <input className="input" required placeholder="Título (ex: Informações dos parceiros)" value={titulo} onChange={(e) => setTitulo(e.target.value)} />
      <textarea className="textarea" rows={2} placeholder="Contexto para o solicitante (opcional)" value={descricao} onChange={(e) => setDescricao(e.target.value)} />
      <div className="text-xs muted strong" style={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>Itens pendentes</div>
      {itens.map((v, i) => (
        <div key={i} style={{ display: 'flex', gap: 6 }}>
          <input className="input" placeholder={`Ex: Logo da Unifenas em PNG`} value={v} onChange={(e) => setItens(itens.map((x, j) => j === i ? e.target.value : x))} />
          {itens.length > 1 && (
            <button type="button" className="btn btn-ghost btn-icon btn-sm" onClick={() => setItens(itens.filter((_, j) => j !== i))}>
              <X size={14} />
            </button>
          )}
        </div>
      ))}
      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setItens([...itens, ''])} style={{ alignSelf: 'flex-start' }}>
        <Plus size={13} /> Adicionar item
      </button>
      {err && <div style={{ color: 'var(--c-danger)', fontSize: 13 }}>{err}</div>}
      <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>Cancelar</button>
        <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
          {saving ? 'Enviando...' : 'Criar pedido e notificar'}
        </button>
      </div>
    </form>
  );
}

function PedidoItem({ pedido, demandaId, podeCriar, podeResponder, onChange }) {
  const resolvido = !!pedido.resolvido_em;
  const cancelado = !!pedido.cancelado_em;
  const totalItens = pedido.itens.length;
  const resolvidos = pedido.itens.filter((i) => i.resolvido).length;

  const cancelar = async () => {
    if (!confirm('Cancelar este pedido de informação?')) return;
    await api.post(`/demandas/${demandaId}/pedidos-info/${pedido.id}/cancelar`);
    onChange?.();
  };

  return (
    <div style={{ padding: 12, border: '1px solid var(--c-border)', borderRadius: 10, background: resolvido ? 'var(--c-success-soft)' : cancelado ? 'var(--c-surface-2)' : 'white' }}>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
            {resolvido ? <Badge color="success"><Check size={11} /> Resolvido</Badge>
              : cancelado ? <Badge color="default">Cancelado</Badge>
              : <Badge color="warning">{resolvidos}/{totalItens} respondidos</Badge>}
            <strong style={{ fontSize: 14 }}>{pedido.titulo}</strong>
          </div>
          <div className="text-xs muted">
            criado por {pedido.criado_por_nome} em {format(new Date(pedido.created_at), 'dd/MM/yyyy HH:mm')}
          </div>
          {pedido.descricao && <div className="text-sm" style={{ marginTop: 6 }}>{pedido.descricao}</div>}
        </div>
        {podeCriar && !resolvido && !cancelado && (
          <button className="btn btn-ghost btn-icon btn-sm" onClick={cancelar} title="Cancelar pedido">
            <X size={14} />
          </button>
        )}
      </div>

      <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {pedido.itens.map((item) => (
          <ItemRow
            key={item.id}
            item={item}
            demandaId={demandaId}
            trancado={resolvido || cancelado}
            podeResponder={podeResponder || podeCriar}
            onChange={onChange}
          />
        ))}
      </div>
    </div>
  );
}

const ACCEPT = 'image/png,image/jpeg,image/gif,image/webp,application/pdf,video/mp4';

function ItemRow({ item, demandaId, trancado, podeResponder, onChange }) {
  const [editing, setEditing] = useState(false);
  const [resposta, setResposta] = useState(item.resposta || '');
  const [arquivoUrl, setArquivoUrl] = useState(item.arquivo_url || '');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);

  const doUpload = async (file) => {
    setUploading(true);
    try {
      const res = await api.upload('/uploads', file, (p) => setProgress(p));
      setArquivoUrl(res.url);
    } catch (e) { alert(e.message); } finally { setUploading(false); }
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 20 * 1024 * 1024) { alert('Arquivo > 20MB'); return; }
    doUpload(f);
  };

  const salvar = async (resolvido) => {
    setSaving(true);
    try {
      await api.patch(`/demandas/${demandaId}/pedidos-info/itens/${item.id}`, {
        resposta: resposta || null,
        arquivo_url: arquivoUrl || null,
        resolvido,
      });
      setEditing(false);
      onChange?.();
    } catch (e) { alert(e.message); } finally { setSaving(false); }
  };

  return (
    <div style={{ padding: 10, border: '1px solid var(--c-border)', borderRadius: 8, background: item.resolvido ? 'var(--c-success-soft)' : 'white' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <div style={{ marginTop: 2 }}>
          {item.resolvido ? <Check size={16} color="var(--c-success)" /> : <div style={{ width: 16, height: 16, borderRadius: 8, border: '2px solid var(--c-border-strong)' }} />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 500, textDecoration: item.resolvido ? 'line-through' : 'none', color: item.resolvido ? 'var(--c-text-muted)' : 'var(--c-text)' }}>
            {item.descricao}
          </div>
          {item.resposta && !editing && (
            <div style={{ marginTop: 4, fontSize: 13, padding: 6, background: 'var(--c-surface-2)', borderRadius: 6 }}>
              💬 {item.resposta}
            </div>
          )}
          {item.arquivo_url && !editing && (
            <a href={item.arquivo_url} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4, color: 'var(--c-primary)', fontSize: 12 }}>
              <Paperclip size={12} /> Ver anexo
            </a>
          )}

          {editing && (
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <textarea className="textarea" rows={2} placeholder="Sua resposta (opcional)" value={resposta} onChange={(e) => setResposta(e.target.value)} />
              <input ref={fileRef} type="file" accept={ACCEPT} onChange={onPick} style={{ display: 'none' }} />
              {arquivoUrl ? (
                <div className="row" style={{ gap: 6 }}>
                  <a href={arquivoUrl} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm" style={{ flex: 1 }}>
                    <Paperclip size={12} /> Anexo enviado
                  </a>
                  <button type="button" className="btn btn-ghost btn-icon btn-sm" onClick={() => setArquivoUrl('')}><X size={14} /></button>
                </div>
              ) : uploading ? (
                <div>
                  <div style={{ height: 4, background: 'var(--c-border)', borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{ width: `${progress}%`, height: '100%', background: 'var(--c-primary)' }} />
                  </div>
                  <div className="text-xs muted">Enviando... {progress}%</div>
                </div>
              ) : (
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => fileRef.current?.click()}>
                  <Upload size={13} /> Anexar arquivo
                </button>
              )}
              <div className="row" style={{ justifyContent: 'flex-end', gap: 6 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => setEditing(false)} disabled={saving}>Cancelar</button>
                <button className="btn btn-primary btn-sm" onClick={() => salvar(true)} disabled={saving}>
                  {saving ? '...' : 'Marcar como resolvido'}
                </button>
              </div>
            </div>
          )}
        </div>
        {!editing && !trancado && podeResponder && !item.resolvido && (
          <button className="btn btn-primary btn-sm" onClick={() => setEditing(true)}>
            Responder
          </button>
        )}
      </div>
    </div>
  );
}
